import { usePlatform } from '@monogrid/vue-boost/core'
import Lenis from '@studio-freight/lenis'
import { useCssVar } from '@vueuse/core'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { MaybeRefOrGetter } from 'vue'
import { computed, onUnmounted, readonly, ref, unref, watch } from 'vue'

import { useGlobalScrollLock } from '@/functions/useGlobalScrollLock'

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

export type SmoothScrollOptions = ConstructorParameters<typeof Lenis>[0]

const DEFAULT_OPTIONS: SmoothScrollOptions = {
  duration: parseFloat(useCssVar('--smooth-scroll-duration').value) || 0.8,
  easing: (t: number) => (t === 1) ? 1 : (1 - Math.pow(2, -10 * t)) // out expo
}

export function useSmoothScroll (customOptions?: MaybeRefOrGetter<SmoothScrollOptions>) {
  const platform = usePlatform()
  const lenis = ref()
  const scrollLocked = useGlobalScrollLock()
  const options = computed(() => ({
    ...DEFAULT_OPTIONS,
    ...unref(customOptions)
  }))

  const update = time => lenis.value?.raf(time * 1000)

  const init = () => {
    if (lenis.value) return
    try {
      lenis.value = new Lenis(unref(options))
      lenis.value.on('scroll', ScrollTrigger.update)
      if (scrollLocked.value && lenis.value.options.wrapper === window) lenis.value.stop()
      gsap.ticker.add(update)
    } catch (error) {
      // The wrapper and content elements provided via options may be template refs.
      // In that case, Lenis instantiation will fail on first tick since they're not mounted yet.
      // console.warn(error.message)
    }
  }

  const destroy = () => {
    if (!lenis.value) return
    gsap.ticker.remove(update)
    lenis.value.destroy()
    lenis.value = undefined
  }

  const scrollTo = (target: unknown, animate = true) => {
    if (!target || target === 0) return
    if (lenis.value && animate) {
      lenis.value.scrollTo(target, {
        easing: (t: number) => 1 - Math.pow(1 - t, 3) // out cubic
      })
    } else {
      const { wrapper = window } = unref(options)
      const fn = (animate === false) ? 'set' : 'to'
      gsap[fn](wrapper, { scrollTo: target })
    }
  }

  const resetScroll = () => {
    const { wrapper = window } = unref(options)
    return gsap.set(wrapper, {
      scrollTo: 0,
      onComplete: () => lenis.value?.scrollTo(0, { immediate: true, force: true })
    })
  }

  const refresh = async () => {
    if (!platform.desktop.value) return
    destroy()
    init()
  }

  watch(platform.desktop, value => value ? init() : destroy(), { immediate: true })

  watch(options, refresh)

  watch(scrollLocked, locked => {
    if (!lenis.value || lenis.value.options.wrapper !== window) return
    lenis.value[locked ? 'stop' : 'start']()
  })

  onUnmounted(destroy)

  return {
    lenis: readonly(lenis),
    refresh,
    resetScroll,
    scrollTo
  }
}
